import MetaIcon from "@/assets/icons/meta.svg";
import InstagramIcon from "@/assets/icons/instagram.svg";
import TiktokIcon from "@/assets/icons/tiktok.png";
import GoogleAdsIcon from "@/assets/icons/google_ads.svg";
import { Platform } from "../creatives/types";
import { Account, ColumnOption, MetricFormula, PlatformType } from "@/types";
import LinkedInIcon from "@/assets/icons/linkedin-logo.png";

const platformIconMap = {
  [Platform.Facebook]: MetaIcon,
  [Platform.Instagram]: InstagramIcon,
  [Platform.Tiktok]: TiktokIcon,
  [Platform.TikTok]: TiktokIcon,
  [Platform.Google]: GoogleAdsIcon,
  [Platform.LinkedIn]: LinkedInIcon,
  [Platform.Linkedin]: LinkedInIcon,
  [PlatformType.GOOGLE]: GoogleAdsIcon,
  [PlatformType.FACEBOOK]: MetaIcon,
  [PlatformType.TIKTOK]: TiktokIcon,
  [PlatformType.LINKEDIN]: LinkedInIcon,
};

export function getPlatformIcon(platform: Platform | PlatformType): string {
  const icon = platformIconMap[platform];
  if (!icon) {
    console.warn(`No icon found for platform: ${platform}`);
    return "";
  }
  return icon;
}

export function capitalizeFirstLetter(str: string): string {
  const temp = str.trim();
  return temp.charAt(0).toUpperCase() + temp.slice(1).toLowerCase();
}

export function formatNumberToString(num: string): string {
  const numFloat = parseFloat(num);
  if (numFloat > 999999) {
    return `${(numFloat / 1000000).toFixed(2)}M`;
  }
  if (numFloat > 9999) {
    return `${(numFloat / 10000).toFixed(2)}K`;
  }
  return `${numFloat.toFixed(2)}`;
}

export const formatNumberWithDash = (number: string | number): string => {
  const numStr = number.toString();
  if (numStr.length <= 3) {
    return numStr;
  }
  const groups = numStr.match(/\d{1,3}/g);

  if (!groups) {
    return numStr;
  }

  if (groups.length > 1 && groups[groups.length - 1].length === 1) {
    groups[groups.length - 2] = groups[groups.length - 2] + groups[groups.length - 1];
    groups.pop();
  }
  return groups.join("-");
};

export function throttle(callback: Function, delay: number) {
  let lastCall = 0;

  return function (...args: any) {
    const now = new Date().getTime();

    if (now - lastCall >= delay) {
      callback(...args);
      lastCall = now;
    }
  };
}

export function isCurrencyPresent(text: string) {
  const regex = /\p{Sc}/gu;
  let match;
  while ((match = regex.exec(text)) !== null) {
    return true;
  }
  return false;
}

export function addCommas(
  arr: { [key: string]: number | string }[],
  annotations: any,
): { [key: string]: string | number }[] {
  try {
    return arr.map((item: { [key: string]: number | string }) => {
      const formattedItem: { [key: string]: string | number } = {};
      for (const key in item) {
        const currencyPresent =
          annotations &&
          key in annotations &&
          isCurrencyPresent(annotations[key].unit_type);
        formattedItem[key] =
          typeof item[key] === "number" && currencyPresent
            ? (item[key]).toLocaleString("en-US", {
                style: "decimal",
              })
            : item[key];
      }
      return formattedItem;
    });
  } catch (error) {
    console.log("Error in adding commas", error);
  }
  return arr;
}

export const roundUpToTwoDecimalPlaces = (number: number): number => {
  const numberString = number.toString();
  const exponentIndex = numberString.indexOf("e");
  if (exponentIndex !== -1) {
    const [mantissa] = numberString.split("e");
    return parseFloat(parseFloat(mantissa).toFixed(2));
  }
  return Math.round(number * 100) / 100;
};

function formatScaledValue(
  value: number,
  suffix: string,
  currencySymbol: string,
  isCurrency: boolean = true,
): string {
  const formattedValue = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
  return isCurrency ? currencySymbol + formattedValue + suffix : formattedValue + suffix;
}

export function formatNumber(
  value: number,
  scale?: boolean,
  isCurrency = true,
  currencyCode = "USD",
): string {
  const threshold = 0.00099;
  const currencyLocaleMap: {
    [key: string]: string;
  } = {
    INR: "en-IN",
    USD: "en-US",
    // Add more currency code to locale mappings here
  };

  const localeCode = currencyLocaleMap[currencyCode];

  if (!localeCode) {
    throw new Error("Unsupported currency code");
  }

  if (Math.abs(value) === 0) {
    return isCurrency
      ? `${new Intl.NumberFormat(localeCode, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: "symbol",
          useGrouping: true,
        }).format(0)}`
      : "0";
  }

  if (Math.abs(value) < threshold) {
    return isCurrency
      ? `≈ ${new Intl.NumberFormat(localeCode, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: "symbol",
          useGrouping: true,
        }).format(0)}`
      : "≈ 0";
  }

  const formattedValue: string = new Intl.NumberFormat(localeCode, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "symbol",
    useGrouping: true,
  }).format(value);
  const formattedSplitValue = formattedValue.split("");

  const currencySymbol = formattedSplitValue[0];

  if (scale === true) {
    const scalingSuffixes: {
      [key: string]: number;
    } = {
      K: 1e3,
      M: 1e6,
      B: 1e9,
    };
    let scaleValue: number = value;

    if (value >= scalingSuffixes.B) {
      scaleValue = value / scalingSuffixes.B;
      return formatScaledValue(scaleValue, "B", currencySymbol, isCurrency);
    }
    if (value >= scalingSuffixes.M) {
      scaleValue = value / scalingSuffixes.M;
      return formatScaledValue(scaleValue, "M", currencySymbol, isCurrency);
    }
    if (value >= scalingSuffixes.K) {
      scaleValue = value / scalingSuffixes.K;
      return formatScaledValue(scaleValue, "K", currencySymbol, isCurrency);
    }
  }

  return isCurrency ? formattedValue : roundUpToTwoDecimalPlaces(value).toString();
}

export const flattenDictValues = (data: Record<string, string[]>): string[] => {
  let flattenedArray: string[] = [];
  for (const key in data) {
    if (Array.isArray(data[key])) {
      flattenedArray = flattenedArray.concat(data[key]);
    } else {
      // @ts-ignore
      flattenedArray.push(data[key]);
    }
  }
  return flattenedArray;
};

export const isImageFile = (filename: string) => {
  const imageExtensions = /\.(jpg|jpeg|png|gif|svg|webp|bmp)$/i;
  return imageExtensions.test(filename);
};

// Sort metrics placing those with sub_type === "EXTERNAL" at the top, then alphabetically by label
export function sortMetricItems<T extends { label: string; sub_type?: string }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    const aIsExternal = a?.sub_type === "EXTERNAL";
    const bIsExternal = b?.sub_type === "EXTERNAL";

    if (aIsExternal && !bIsExternal) return -1;
    if (!aIsExternal && bIsExternal) return 1;

    return a.label.localeCompare(b.label);
  });
}

/**
 * Updates an account in the account list if it exists
 * @param accountList - Current list of accounts
 * @param updatedAccount - The updated account to replace in the list
 * @param setAccountList - Function to update the account list
 */
export function updateAccountInList(
  accountList: Account[],
  updatedAccount: Account | undefined,
  setAccountList: (accounts: Account[]) => void,
): void {
  if (!updatedAccount) return;

  const accountIndex = accountList.findIndex(
    (account) => account.id === updatedAccount.id,
  );
  if (accountIndex !== -1) {
    const updatedAccountList = [...accountList];
    updatedAccountList[accountIndex] = updatedAccount;
    setAccountList(updatedAccountList);
  }
}

import {
  IconFishHook,
  IconMovie,
  IconPhoto,
  IconAlignLeft,
  IconScanEye,
  IconCircuitBattery,
  IconGalaxy,
  IconLanguage,
  IconPhotoVideo,
} from "@tabler/icons-react";
import { RiImage2Line, RiMovieLine } from "@remixicon/react";

// Function to get appropriate icon based on label content
export const getOverviewIcon = (label: string, isDefault: boolean) => {
  if (!isDefault) {
    return IconGalaxy;
  }
  switch (label) {
    case "Video Overview":
      return IconMovie;
    case "Audio Overview":
      return IconCircuitBattery;
    case "Script":
      return IconAlignLeft;
    case "Hook":
      return IconFishHook;
    case "Image Overview":
      return IconPhoto;
    case "Text":
      return IconLanguage;
    case "Visuals":
      return IconScanEye;
    default:
      return IconGalaxy;
  }
};

/**
 * Returns unique colors for labels based on their index
 * @param index - The index of the label (0-based)
 * @returns Object containing background, border, and text colors
 */
export const getLabelColors = (index: number) => {
  const colorSchemes = [
    {
      background: "#5344FF0D",
      border: "#5344FF1A",
      text: "#5344FF",
      icon: "#5344FF66",
    },
    {
      background: "#009B4D0D",
      border: "#009B4D1A",
      text: "#009B4D",
      icon: "#009B4D66",
    },
    {
      background: "#FF46990D",
      border: "#FF46991A",
      text: "#FF4699",
      icon: "#FF469966",
    },
    {
      background: "#4280EF0D",
      border: "#4280EF1A",
      text: "#4280EF",
      icon: "#4280EF66",
    },
    {
      background: "#00A48B0D",
      border: "#00A48B1A",
      text: "#00A48B",
      icon: "#00A48B66",
    },
    {
      background: "#FF45000D",
      border: "#FF45001A",
      text: "#FF4500",
      icon: "#FF450066",
    },
    {
      background: "#A259FF0D",
      border: "#A259FF1A",
      text: "#A259FF",
      icon: "#A259FF66",
    },
    {
      background: "#C000000D",
      border: "#C000001A",
      text: "#C00000",
      icon: "#C0000066",
    },
    {
      background: "#9D174D0D",
      border: "#9D174D1A",
      text: "#9D174D",
      icon: "#9D174D66",
    },
    {
      background: "#C2410C0D",
      border: "#C2410C1A",
      text: "#C2410C",
      icon: "#C2410C66",
    },
    {
      background: "#EA580C0D",
      border: "#EA580C1A",
      text: "#EA580C",
      icon: "#EA580C66",
    },
    {
      background: "#6B8E000D",
      border: "#6B8E001A",
      text: "#6B8E00",
      icon: "#6B8E0066",
    },
    {
      background: "#A162070D",
      border: "#A162071A",
      text: "#A16207",
      icon: "#A1620766",
    },
    {
      background: "#65A30D0D",
      border: "#65A30D1A",
      text: "#65A30D",
      icon: "#65A30D66",
    },
    {
      background: "#3F62120D",
      border: "#3F62121A",
      text: "#3F6212",
      icon: "#3F621266",
    },
    {
      background: "#16A34A0D",
      border: "#16A34A1A",
      text: "#16A34A",
      icon: "#16A34A66",
    },
    {
      background: "#0D94880D",
      border: "#0D94881A",
      text: "#0D9488",
      icon: "#0D948866",
    },
    {
      background: "#0891B20D",
      border: "#0891B21A",
      text: "#0891B2",
      icon: "#0891B266",
    },
    {
      background: "#0EA5E90D",
      border: "#0EA5E91A",
      text: "#0EA5E9",
      icon: "#0EA5E966",
    },
    {
      background: "#1D4ED80D",
      border: "#1D4ED81A",
      text: "#1D4ED8",
      icon: "#1D4ED866",
    },
    {
      background: "#1E40AF0D",
      border: "#1E40AF1A",
      text: "#1E40AF",
      icon: "#1E40AF66",
    },
    {
      background: "#4338CA0D",
      border: "#4338CA1A",
      text: "#4338CA",
      icon: "#4338CA66",
    },
    {
      background: "#7C3AED0D",
      border: "#7C3AED1A",
      text: "#7C3AED",
      icon: "#7C3AED66",
    },
    {
      background: "#9333EA0D",
      border: "#9333EA1A",
      text: "#9333EA",
      icon: "#9333EA66",
    },
    {
      background: "#C026D30D",
      border: "#C026D31A",
      text: "#C026D3",
      icon: "#C026D366",
    },
    {
      background: "#DB27770D",
      border: "#DB27771A",
      text: "#DB2777",
      icon: "#DB277766",
    },
    {
      background: "#E11D480D",
      border: "#E11D481A",
      text: "#E11D48",
      icon: "#E11D4866",
    },
    {
      background: "#8B5E3C0D",
      border: "#8B5E3C1A",
      text: "#8B5E3C",
      icon: "#8B5E3C66",
    },
  ];

  // Use modulo to cycle through colors if index exceeds available schemes
  const colorIndex = index % colorSchemes.length;
  return colorSchemes[colorIndex];
};

export const getMediaIcon = (
  type: "image" | "video" | "mixed" | null,
  includeOuterDiv: boolean = true,
  iconSize: number = 14,
  outerDivSize: number = 20,
) => {
  const iconMap = {
    image: {
      icon: <RiImage2Line size={iconSize} className="text-[#FF4699]" />,
      bg: "bg-[#FF469924]",
    },
    video: {
      icon: <RiMovieLine size={iconSize} className="text-[#018C62]" />,
      bg: "bg-[#018C6224]",
    },
    mixed: {
      icon: <IconPhotoVideo size={iconSize} className="text-[#5344FF]" />,
      bg: "bg-[#5344FF24]",
    },
  };

  // Default to mixed if type is null
  const mediaType = type || "mixed";
  const mediaData = iconMap[mediaType];

  if (!mediaData) {
    // Fallback to mixed if mediaType is not found
    const fallbackData = iconMap.mixed;
    const icon = fallbackData.icon;
    const bg = fallbackData.bg;

    if (!includeOuterDiv) {
      return icon;
    }

    return (
      <div
        className={`flex justify-center items-center squircle-mask squircle-radius-[6px] ${bg}`}
        style={{
          height: `${outerDivSize}px`,
          width: `${outerDivSize}px`,
        }}
      >
        {icon}
      </div>
    );
  }

  const icon = mediaData.icon;
  const bg = mediaData.bg;

  if (!includeOuterDiv) {
    return icon;
  }

  return (
    <div
      className={`flex justify-center items-center squircle-mask squircle-radius-[6px] ${bg}`}
      style={{
        height: `${outerDivSize}px`,
        width: `${outerDivSize}px`,
      }}
    >
      {icon}
    </div>
  );
};

/**
 * Gets a human-readable formula string using metric labels instead of UUIDs
 * @param metricFormula - The metric formula object containing formula string and primary metric IDs
 * @param metricsOptions - Array of available metrics to get labels from
 * @returns Formatted formula string with metric labels
 */
export const getMetricFormulaLabel = (
  metricFormula: MetricFormula | null,
  metricsOptions: ColumnOption[],
): string | null => {
  if (!metricFormula || !metricFormula?.formula) return null;

  // Create a map of metric IDs to their labels
  const metricsMap = (metricFormula.primary_metric_ids ?? []).reduce<
    Record<string, ColumnOption>
  >((acc, metricId) => {
    const metric = metricsOptions.find((m) => m.key === metricId);
    if (metric) {
      acc[metricId] = metric;
    }
    return acc;
  }, {});

  const displayLabel = formatMetricDisplayFormula(metricFormula.formula, metricsMap);
  return displayLabel;
};

/**
 * Formats a metric formula string by replacing UUIDs with metric names and cleaning up unnecessary brackets
 * @param formula - The formula string containing UUIDs and operators
 * @param metricsMap - Map of metric UUIDs to their corresponding ColumnOption objects
 * @returns Formatted formula string with metric names instead of UUIDs
 */

export const formatMetricDisplayFormula = (
  formula: string | null,
  metricsMap: Record<string, ColumnOption>,
): string | null => {
  if (!formula) return null;

  // Helper to process individual terms
  const processTerm = (term: string): string => {
    // Remove unnecessary brackets around single terms
    const cleanedTerm = term.replace(/^\((.*)\)$/, (_match: string, inner: string) => {
      // Only remove brackets if there's no operator inside
      const hasOperator =
        typeof inner === "string" && (inner.includes("*") || inner.includes("/"));
      return hasOperator ? `(${inner})` : inner;
    });

    // Replace UUIDs with metric names
    return cleanedTerm.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      (uuid) => {
        return metricsMap[uuid]?.label || uuid;
      },
    );
  };

  // Split formula into terms while preserving operators
  let processedFormula = formula;

  // Process terms in brackets first
  processedFormula = processedFormula.replace(/\([^()]+\)/g, (match) => {
    return processTerm(match);
  });

  // Process remaining terms
  processedFormula = processTerm(processedFormula);

  // Clean up any remaining unnecessary brackets
  processedFormula = processedFormula.replace(/\(\(([^()]*)\)\)/g, "($1)");

  // Add spaces around operators (* and /)
  processedFormula = processedFormula
    .replace(/\*/g, " * ")
    .replace(/\//g, " / ")
    // Clean up any double spaces that might have been created
    .replace(/\s+/g, " ")
    // Clean up spaces inside parentheses
    .replace(/\(\s+/g, "(")
    .replace(/\s+\)/g, ")");

  return processedFormula;
};
